'use client';

import { c as _c } from "react/compiler-runtime";
import type { ButtonProps, TextProps } from '@chakra-ui/react';
import { Button, Pagination as ChakraPagination, IconButton, Text, createContext, usePaginationContext } from '@chakra-ui/react';
import * as React from 'react';
import { HiChevronLeft, HiChevronRight, HiMiniEllipsisHorizontal } from 'react-icons/hi2';
import { LinkButton } from './link-button';
interface ButtonVariantMap {
  current: ButtonProps['variant'];
  default: ButtonProps['variant'];
  ellipsis: ButtonProps['variant'];
}
type PaginationVariant = 'outline' | 'solid' | 'subtle';
interface ButtonVariantContext {
  size: ButtonProps['size'];
  variantMap: ButtonVariantMap;
  getHref?: (page: number) => string;
}
const [RootPropsProvider, useRootProps] = createContext<ButtonVariantContext>({
  name: 'RootPropsProvider'
});
export interface PaginationRootProps extends Omit<ChakraPagination.RootProps, 'type'> {
  size?: ButtonProps['size'];
  variant?: PaginationVariant;
  getHref?: (page: number) => string;
}
const variantMap: Record<PaginationVariant, ButtonVariantMap> = {
  outline: {
    default: 'ghost',
    ellipsis: 'plain',
    current: 'outline'
  },
  solid: {
    default: 'outline',
    ellipsis: 'outline',
    current: 'solid'
  },
  subtle: {
    default: 'ghost',
    ellipsis: 'plain',
    current: 'subtle'
  }
};
export const PaginationRoot = React.forwardRef<HTMLDivElement, PaginationRootProps>(function PaginationRoot(props, ref) {
  const $ = _c(16);
  let getHref;
  let rest;
  let t0;
  let t1;
  if ($[0] !== props) {
    ({
      size: t0,
      variant: t1,
      getHref,
      ...rest
    } = props);
    $[0] = props;
    $[1] = getHref;
    $[2] = rest;
    $[3] = t0;
    $[4] = t1;
  } else {
    getHref = $[1];
    rest = $[2];
    t0 = $[3];
    t1 = $[4];
  }
  const size = t0 === undefined ? "sm" : t0;
  const variant = t1 === undefined ? "outline" : t1;
  const t2 = variantMap[variant];
  let t3;
  if ($[5] !== getHref || $[6] !== size || $[7] !== t2) {
    t3 = {
      size,
      variantMap: t2,
      getHref
    };
    $[5] = getHref;
    $[6] = size;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = getHref ? "link" : "button";
  let t5;
  if ($[9] !== ref || $[10] !== rest || $[11] !== t4) {
    t5 = <ChakraPagination.Root ref={ref} type={t4} {...rest} />;
    $[9] = ref;
    $[10] = rest;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t5) {
    t6 = <RootPropsProvider value={t3}>{t5}</RootPropsProvider>;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
});
export const PaginationEllipsis = React.forwardRef<HTMLDivElement, ChakraPagination.EllipsisProps>(function PaginationEllipsis(props, ref) {
  const $ = _c(8);
  const {
    size,
    variantMap
  } = useRootProps();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <HiMiniEllipsisHorizontal />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== size || $[2] !== variantMap.ellipsis) {
    t1 = <Button as="span" size={size} variant={variantMap.ellipsis}>{t0}</Button>;
    $[1] = size;
    $[2] = variantMap.ellipsis;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props || $[5] !== ref || $[6] !== t1) {
    t2 = <ChakraPagination.Ellipsis ref={ref} {...props} asChild={true}>{t1}</ChakraPagination.Ellipsis>;
    $[4] = props;
    $[5] = ref;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
});
export const PaginationItem = React.forwardRef<HTMLButtonElement, ChakraPagination.ItemProps>(function PaginationItem(props, ref) {
  const $ = _c(16);
  const {
    page
  } = usePaginationContext();
  const {
    size,
    variantMap,
    getHref
  } = useRootProps();
  const current = page === props.value;
  const variant = current ? variantMap.current : variantMap.default;
  if (getHref) {
    let t0;
    if ($[0] !== getHref || $[1] !== props.value) {
      t0 = getHref(props.value);
      $[0] = getHref;
      $[1] = props.value;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    let t1;
    if ($[3] !== props.value || $[4] !== size || $[5] !== t0 || $[6] !== variant) {
      t1 = <LinkButton href={t0} size={size} variant={variant}>{props.value}</LinkButton>;
      $[3] = props.value;
      $[4] = size;
      $[5] = t0;
      $[6] = variant;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    return t1;
  }
  let t0;
  if ($[8] !== props.value || $[9] !== size || $[10] !== variant) {
    t0 = <Button size={size} variant={variant}>{props.value}</Button>;
    $[8] = props.value;
    $[9] = size;
    $[10] = variant;
    $[11] = t0;
  } else {
    t0 = $[11];
  }
  let t1;
  if ($[12] !== props || $[13] !== ref || $[14] !== t0) {
    t1 = <ChakraPagination.Item ref={ref} {...props} asChild={true}>{t0}</ChakraPagination.Item>;
    $[12] = props;
    $[13] = ref;
    $[14] = t0;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  return t1;
});
export const PaginationPrevTrigger = React.forwardRef<HTMLButtonElement, ChakraPagination.PrevTriggerProps>(function PaginationPrevTrigger(props, ref) {
  const $ = _c(16);
  const {
    size,
    variantMap,
    getHref
  } = useRootProps();
  const {
    previousPage
  } = usePaginationContext();
  if (getHref) {
    let t0;
    if ($[0] !== getHref || $[1] !== previousPage) {
      t0 = previousPage != null ? getHref(previousPage) : undefined;
      $[0] = getHref;
      $[1] = previousPage;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <HiChevronLeft />;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== size || $[5] !== t0 || $[6] !== variantMap.default) {
      t2 = <LinkButton href={t0} size={size} variant={variantMap.default}>{t1}</LinkButton>;
      $[4] = size;
      $[5] = t0;
      $[6] = variantMap.default;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t0;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <HiChevronLeft />;
    $[8] = t0;
  } else {
    t0 = $[8];
  }
  let t1;
  if ($[9] !== size || $[10] !== variantMap.default) {
    t1 = <IconButton size={size} variant={variantMap.default}>{t0}</IconButton>;
    $[9] = size;
    $[10] = variantMap.default;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== props || $[13] !== ref || $[14] !== t1) {
    t2 = <ChakraPagination.PrevTrigger ref={ref} asChild={true} {...props}>{t1}</ChakraPagination.PrevTrigger>;
    $[12] = props;
    $[13] = ref;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
});
export const PaginationNextTrigger = React.forwardRef<HTMLButtonElement, ChakraPagination.NextTriggerProps>(function PaginationNextTrigger(props, ref) {
  const $ = _c(16);
  const {
    size,
    variantMap,
    getHref
  } = useRootProps();
  const {
    nextPage
  } = usePaginationContext();
  if (getHref) {
    let t0;
    if ($[0] !== getHref || $[1] !== nextPage) {
      t0 = nextPage != null ? getHref(nextPage) : undefined;
      $[0] = getHref;
      $[1] = nextPage;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <HiChevronRight />;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== size || $[5] !== t0 || $[6] !== variantMap.default) {
      t2 = <LinkButton href={t0} size={size} variant={variantMap.default}>{t1}</LinkButton>;
      $[4] = size;
      $[5] = t0;
      $[6] = variantMap.default;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t0;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <HiChevronRight />;
    $[8] = t0;
  } else {
    t0 = $[8];
  }
  let t1;
  if ($[9] !== size || $[10] !== variantMap.default) {
    t1 = <IconButton size={size} variant={variantMap.default}>{t0}</IconButton>;
    $[9] = size;
    $[10] = variantMap.default;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== props || $[13] !== ref || $[14] !== t1) {
    t2 = <ChakraPagination.NextTrigger ref={ref} asChild={true} {...props}>{t1}</ChakraPagination.NextTrigger>;
    $[12] = props;
    $[13] = ref;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
});
export const PaginationItems = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <ChakraPagination.Context>{t1 => {
        const {
          pages
        } = t1;
        return pages.map((page, index) => page.type === "ellipsis" ? <PaginationEllipsis key={index} index={index} {...props} /> : <PaginationItem key={index} type="page" value={page.value} {...props} />);
      }}</ChakraPagination.Context>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
interface PageTextProps extends TextProps {
  format?: 'short' | 'compact' | 'long';
}
export const PaginationPageText = React.forwardRef<HTMLParagraphElement, PageTextProps>(function PaginationPageText(props, ref) {
  const $ = _c(7);
  let rest;
  let t0;
  if ($[0] !== props) {
    ({
      format: t0,
      ...rest
    } = props);
    $[0] = props;
    $[1] = rest;
    $[2] = t0;
  } else {
    rest = $[1];
    t0 = $[2];
  }
  const format = t0 === undefined ? "compact" : t0;
  const {
    page,
    totalPages,
    pageRange,
    count
  } = usePaginationContext();
  let t1;
  bb0: {
    if (format === "short") {
      t1 = `${page} / ${totalPages}`;
      break bb0;
    }
    if (format === "compact") {
      t1 = `${page} of ${totalPages}`;
      break bb0;
    }
    t1 = `${pageRange.start + 1} - ${Math.min(pageRange.end, count)} of ${count}`;
  }
  const content = t1;
  let t2;
  if ($[3] !== content || $[4] !== ref || $[5] !== rest) {
    t2 = <Text ref={ref} fontWeight="medium" {...rest}>{content}</Text>;
    $[3] = content;
    $[4] = ref;
    $[5] = rest;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
});